import React from "react";
import { Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Header } from "./components/Header/Header";
import { Grid } from "@/domains/Grid/Grid";
import { PostsView } from "./domains/PostCreator/PostsView";
import { StoriesView } from "./domains/PostCreator/StoriesView";
import { LibraryViewLayout } from "@/domains/LibraryView/LibraryViewLayout";
import { ImageLibrary } from "@/domains/LibraryView/components/ImageLibrary";
import { PostVideoLibrary } from "@/domains/LibraryView/components/PostVideoLibrary";
import { ShortVideoLibrary } from "@/domains/LibraryView/components/ShortVideoLibrary";
import { LongVideoLibrary } from "@/domains/LibraryView/components/LongVideoLibrary";
import { GeneralVideoLibrary } from "@/domains/LibraryView/components/GeneralVideoLibrary";
import { AudioLibrary } from "@/domains/LibraryView/components/AudioLibrary";
import { AssetsLibrary } from "@/domains/Assets/AssetsLibrary";
import { PostSelectorModal } from "@/domains/PostSelector/PostSelectorModal";
import { ImageEditorModal } from "./components/ImageEditor/ImageEditorModal";
import { PostCreatorModalContainer } from "@/domains/PostCreator/PostCreatorModalContainer";
import { AuthGuard } from "./components/Auth/AuthGuard";
import { useAppStore } from "@/core/store/useAppStore";
import { useTokenValidation } from "@/hooks/useTokenValidation";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import { useJobCompletionHandler } from "@/hooks/useJobCompletionHandler";
import { ADMIN_CANVAS, useCanvasColor } from "@/hooks/useCanvasColor";
import { DownloadNotificationManager } from "./components/ui/DownloadNotificationManager";
import { ClientProvider } from "./contexts/ClientContext";
import { HttpClientProvider } from "@/core/http/HttpClientContext";
import { apiClient } from "@/services/apiClient";
import type { HttpClient } from "@/core/http/types";
import { ReportingView } from "./domains/Reporting/ReportingView";
import { HomeView } from "./domains/Home/HomeView";
import { ClientSettingsView } from "./domains/Client/ClientSettingsView";
import { homeTabRedirect } from "./domains/Home/homeLinks";
import { IdeasView, IdeaDetailView } from "./domains/Ideas";
import { BriefsView, BriefDetailView } from "./domains/Briefs";
import { AdsView } from "./domains/Ads/AdsView";
import { ClientFinancePage } from "./domains/Finance/clientPage/ClientFinancePage";
import { TrailDisplayProvider } from "@/core/navigation/TrailDisplayContext";

/** Old /home/<tab> URLs → the workspace (with panel anchor) or /settings. */
function HomeTabRedirect() {
  const { clientId, tab } = useParams<{ clientId: string; tab: string }>();
  if (!clientId || !tab) return <Navigate to=".." replace />;
  return <Navigate to={homeTabRedirect(tab, clientId)} replace />;
}

export function MainApp() {
  const location = useLocation();
  const { clientId: urlClientId } = useParams<{ clientId: string }>();
  const setCurrentView = useAppStore((state) => state.setCurrentView);
  const selectedClientId = useAppStore((state) => state.selectedClient?.id);
  const clients = useAppStore((state) => state.clients);
  const setSelectedClient = useAppStore((state) => state.setSelectedClient);
  useTokenValidation();
  // The status-bar strip and the page behind the shell match its gray (spec C3).
  useCanvasColor(ADMIN_CANVAS);

  // Auto-select client from URL param
  React.useEffect(() => {
    if (urlClientId && urlClientId !== selectedClientId && clients.length > 0) {
      const clientFromUrl = clients.find((c) => c.id === urlClientId);
      if (clientFromUrl) {
        setSelectedClient(clientFromUrl);
      }
    }
  }, [urlClientId, selectedClientId, clients, setSelectedClient]);

  // Establish realtime sync connection for live updates
  const { isConnected, connectionState } = useRealtimeSync(selectedClientId);

  // Centralized job completion handler - shows toasts and triggers refetches
  useJobCompletionHandler({ clientId: selectedClientId });

  // Log connection status changes
  React.useEffect(() => {
    if (isConnected) {
      console.log('🔌 Realtime sync active - UI will update automatically');
    } else if (connectionState === 'error') {
      console.warn('⚠️ Realtime sync disconnected - will auto-retry');
    }
  }, [isConnected, connectionState]);

  // Sync current view with route
  // Routes are now relative to /app/:clientId
  React.useEffect(() => {
    const path = location.pathname;
    // Extract the view from path like /app/client-id/home -> home
    const viewMatch = path.match(/\/app\/[^/]+\/(.+)/);
    const view = viewMatch ? viewMatch[1] : '';

    if (view.startsWith('home') || path.endsWith(`/${urlClientId}`)) {
      setCurrentView('home');
    } else if (view === 'grid') {
      setCurrentView('grid');
    } else if (view.startsWith('library')) {
      setCurrentView('library');
    } else if (view === 'posts') {
      setCurrentView('posts');
    } else if (view === 'reporting') {
      setCurrentView('reporting');
    } else if (view.startsWith('ideas')) {
      setCurrentView('ideas');
    } else if (view.startsWith('briefs')) {
      setCurrentView('briefs');
    } else if (view === 'ads') {
      setCurrentView('ads');
    } else if (view === 'finance') {
      setCurrentView('finance');
    }
  }, [location.pathname, setCurrentView, urlClientId]);

  const renderMainContent = () => {
    // Routes are relative to /app/:clientId
    return (
      <Routes>
        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<HomeView />} />
        <Route path="home/:tab" element={<HomeTabRedirect />} />
        <Route path="settings" element={<ClientSettingsView />} />
        <Route path="grid" element={<Grid />} />
        <Route path="ads" element={<AdsView />} />
        <Route path="finance" element={<ClientFinancePage />} />
        <Route path="library/*" element={<LibraryViewLayout />}>
          <Route index element={<Navigate to="photos" replace />} />
          <Route path="photos" element={<ImageLibrary />} />
          <Route path="post-videos" element={<PostVideoLibrary />} />
          <Route path="stories" element={<ShortVideoLibrary />} />
          <Route path="reels" element={<LongVideoLibrary />} />
          <Route path="videos" element={<GeneralVideoLibrary />} />
          <Route path="audio" element={<AudioLibrary />} />
          <Route path="assets" element={<AssetsLibrary />} />
        </Route>
        <Route path="stories" element={<StoriesView />} />
        <Route path="posts" element={<PostsView />} />
        <Route path="reporting" element={<ReportingView />} />
        <Route path="ideas" element={<IdeasView />} />
        <Route path="ideas/:ideaId" element={<IdeaDetailView />} />
        <Route path="briefs" element={<BriefsView />} />
        <Route path="briefs/:briefId" element={<BriefDetailView />} />
      </Routes>
    );
  };

  return (
    <HttpClientProvider client={apiClient as unknown as HttpClient}>
    <ClientProvider>
      <AuthGuard>
        <TrailDisplayProvider>
          {/* Contained shell, as in AdminLayout: `relative` clips absolutely
              positioned descendants, `h-dvh` matches iOS's visible height. `overflow-clip`, not `hidden`:
              hidden is still a scroll container, so code (scrollIntoView,
              focus) could scroll the shell where the user cannot scroll back. */}
          <div className="relative h-dvh flex flex-col bg-gray-100 overflow-clip">
            <Header />
            <main className="flex-1 overflow-hidden">
              {renderMainContent()}
            </main>
            {/* All Modals */}
            <PostSelectorModal />
            <PostCreatorModalContainer />
            <ImageEditorModal />
            {/* Download Notifications */}
            <DownloadNotificationManager />
          </div>
        </TrailDisplayProvider>
      </AuthGuard>
    </ClientProvider>
    </HttpClientProvider>
  );
}
